import {
  useCallback, useLayoutEffect, useRef, useState,
} from 'react'
import { useAppDispatch } from '../../hooks'
import {
  Filter,
  getGroupMamoriosWithoutUpdatingState,
  getTeamMamoriosWithoutUpdatingState,
} from '../../modules/Mamorio'
import { getTeamGroups } from '../../modules/Team'
import { toId } from '../../utils/commonUtils'

type DashboardData = {
  scope: string
  mamorios: IMamorio[]
  loading: boolean
  error: string | null
}

const useDashboardMamorios = (
  groupId: string | undefined,
  groups: IGroup[] | null | undefined,
) => {
  const dispatch = useAppDispatch()
  // child_groups には閲覧権限のないグループも含まれるため、認可済みの直下の一覧だけを使う。
  const accessibleGroupIds = groups?.map((group) => String(group.id)).sort().join(',')
  const scope = `${groupId ?? 'team'}:${accessibleGroupIds ?? 'pending'}`
  const requestId = useRef(0)
  const [data, setData] = useState<DashboardData>({
    scope,
    mamorios: [],
    loading: groups == null,
    error: null,
  })

  useLayoutEffect(() => () => {
    // グループ切り替え・アンマウント後に古いレスポンスを採用しない。
    requestId.current += 1
  }, [scope])

  const loadMamorios = useCallback(async (filter: Filter): Promise<IMamorio[]> => {
    requestId.current += 1
    const currentRequestId = requestId.current
    const isCurrentRequest = () => requestId.current === currentRequestId
    setData({
      scope, mamorios: [], loading: true, error: null,
    })

    try {
      if (accessibleGroupIds === undefined) {
        // 認証時にグループ取得が失敗しても、更新ボタンから取得し直せる。
        // 更新された groups による次の呼び出しで MAMORIO を取得する。
        await dispatch(getTeamGroups(true)).unwrap()
        return []
      }

      const accessibleIds = new Set(accessibleGroupIds.split(',').filter(Boolean))
      if (groupId && !accessibleIds.has(groupId)) {
        throw new Error('このグループのダッシュボードを表示する権限がありません。')
      }

      const byId = new Map<string, IMamorio>()
      if (accessibleIds.size > 0) {
        let page = 1
        let totalPages = 1
        do {
          const pageFilter = { ...filter, page, maxMamorios: true }
          const request = groupId
            ? getGroupMamoriosWithoutUpdatingState({ groupId: toId(groupId), filter: pageFilter })
            : getTeamMamoriosWithoutUpdatingState({ filter: pageFilter })
          // 次のページの要否は直前のレスポンスで判定する。
          // eslint-disable-next-line no-await-in-loop
          const result = await dispatch(request).unwrap()
          if (!isCurrentRequest()) return []

          result.mamorios.forEach((mamorio) => {
            const mamorioGroupId = String(mamorio.group_id)
            if (accessibleIds.has(mamorioGroupId) && (!groupId || mamorioGroupId === groupId)) {
              byId.set(String(mamorio.id), mamorio)
            }
          })
          totalPages = result.totalPages
          page += 1
        } while (page <= totalPages)
      }

      const mamorios = Array.from(byId.values())
      setData({
        scope, mamorios, loading: false, error: null,
      })
      return mamorios
    } catch (error: unknown) {
      if (!isCurrentRequest()) return []

      let message = 'MAMORIO一覧の取得に失敗しました。'
      if (typeof error === 'string') message = error
      if (error instanceof Error) message = error.message
      setData({
        scope, mamorios: [], loading: false, error: message,
      })
      throw new Error(message)
    }
  }, [accessibleGroupIds, dispatch, groupId, scope])

  return {
    mamorios: data.scope === scope ? data.mamorios : [],
    loading: data.scope !== scope || data.loading,
    error: data.scope === scope ? data.error : null,
    loadMamorios,
  }
}

export default useDashboardMamorios
